import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { Branch, NewBranch } from "@/api/queries";
import { useCreateBranch, useLocations } from "@/api/queries";
import { Dialog } from "@/components/Dialog";
import { Button, FormField, Input, Select } from "@/components/ui";

/**
 * Screen 09 of the Modernist design — *Agregar sucursal* (.docs/08 §7 F3).
 *
 * `Branch` field for field, which is why .docs/08 §3 lists it among the places
 * the design and the models already agreed. The one thing it adds is `kind`,
 * the Matriz / Planta / CEDIS column, which the legacy schema encoded in the
 * name and F3 gives a column of its own.
 */

const KINDS = ["1", "2", "3", "4", "5"] as const;

type Draft = {
  name: string;
  kind: string;
  location: string;
  address: string;
  neighborhood: string;
  postal_code: string;
  sales_phone: string;
  sales_email: string;
  purchasing_email: string;
};

const EMPTY: Draft = {
  name: "Matriz", // the model's own default, and the commonest first branch
  kind: "1",
  location: "",
  address: "",
  neighborhood: "",
  postal_code: "",
  sales_phone: "",
  sales_email: "",
  purchasing_email: "",
};

export function NuevaSucursal({
  open,
  onClose,
  companyId,
  companyName,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  companyId: number;
  companyName: string;
  /** Told what was created, for a host that carries on from it. */
  onCreated?: (branch: Branch) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [locationSearch, setLocationSearch] = useState("");
  const locations = useLocations(locationSearch);
  const create = useCreateBranch(companyId);

  useEffect(() => {
    if (open) {
      setDraft(EMPTY);
      setLocationSearch("");
      setError(null);
    }
  }, [open]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  async function save() {
    if (!draft.name.trim()) {
      setError(t("newBranch.nameRequired"));
      return;
    }
    setError(null);
    const body: NewBranch = {
      company: companyId,
      name: draft.name.trim(),
      kind: Number(draft.kind) as NonNullable<NewBranch["kind"]>,
      location: draft.location ? Number(draft.location) : null,
      address: draft.address.trim(),
      neighborhood: draft.neighborhood.trim(),
      postal_code: draft.postal_code.trim(),
      // Blank rather than omitted: the field is `blank=True` on the model, and
      // an empty string is what "no number" looks like there.
      sales_phone: draft.sales_phone.trim(),
      sales_email: draft.sales_email.trim(),
      purchasing_email: draft.purchasing_email.trim(),
    };
    try {
      const saved = await create.mutateAsync(body);
      onClose();
      onCreated?.(saved);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("errors.saveBranch"));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      kicker={companyName}
      title={t("newBranch.title")}
      footer={
        <>
          <Button
            variant="primary"
            disabled={create.isPending}
            onClick={() => void save()}
          >
            {create.isPending ? t("newBranch.saving") : t("newBranch.save")}
          </Button>
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}

      <div className="grid gap-5 sm:grid-cols-2 sm:gap-x-6">
        <FormField label={t("newBranch.name")}>
          {(id) => (
            <Input
              id={id}
              value={draft.name}
              maxLength={100}
              data-autofocus=""
              onChange={(event) => set("name", event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t("newBranch.kind")}>
          {(id) => (
            <Select
              id={id}
              value={draft.kind}
              onChange={(event) => set("kind", event.target.value)}
            >
              {KINDS.map((value) => (
                <option key={value} value={value}>
                  {t(`branchKind.${value}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        {/* Two controls for one field, because the catalogue is some two and a
            half thousand municipalities. A single select would be unusable and
            a datalist would key on a name that repeats across states. */}
        <FormField label={t("newBranch.locationSearch")}>
          {(id) => (
            <Input
              id={id}
              type="search"
              value={locationSearch}
              placeholder={t("newBranch.locationSearchPlaceholder")}
              onChange={(event) => setLocationSearch(event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t("newBranch.location")} hint={t("newBranch.locationHint")}>
          {(id) => (
            <Select
              id={id}
              value={draft.location}
              onChange={(event) => set("location", event.target.value)}
            >
              <option value="">{t("newBranch.noLocation")}</option>
              {(locations.data?.results ?? []).map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.short_city}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <div className="sm:col-span-2">
          <FormField label={t("newBranch.address")}>
            {(id) => (
              <Input
                id={id}
                value={draft.address}
                maxLength={100}
                placeholder={t("newBranch.addressExample")}
                onChange={(event) => set("address", event.target.value)}
              />
            )}
          </FormField>
        </div>

        <FormField label={t("newBranch.neighborhood")}>
          {(id) => (
            <Input
              id={id}
              value={draft.neighborhood}
              maxLength={100}
              placeholder={t("newBranch.neighborhoodExample")}
              onChange={(event) => set("neighborhood", event.target.value)}
            />
          )}
        </FormField>

        <FormField label={t("newBranch.postalCode")}>
          {(id) => (
            <Input
              id={id}
              value={draft.postal_code}
              maxLength={20}
              placeholder={t("newBranch.postalCodeExample")}
              onChange={(event) => set("postal_code", event.target.value)}
            />
          )}
        </FormField>
      </div>

      <section className="mt-6 border-t-2 border-rule pt-5">
        <h4 className="mb-1 text-[17px]">{t("newBranch.contactTitle")}</h4>
        <p className="mb-4 text-[13px] text-mute-600">{t("newBranch.contactLead")}</p>
        <div className="grid gap-5 sm:grid-cols-2 sm:gap-x-6">
          <FormField
            label={t("newBranch.salesPhone")}
            hint={t("newBranch.salesPhoneHint")}
          >
            {(id) => (
              <Input
                id={id}
                type="tel"
                value={draft.sales_phone}
                placeholder={t("newBranch.salesPhoneExample")}
                onChange={(event) => set("sales_phone", event.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("newBranch.salesEmail")}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={draft.sales_email}
                maxLength={80}
                placeholder={t("newBranch.salesEmailExample")}
                onChange={(event) => set("sales_email", event.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("newBranch.purchasingEmail")}>
            {(id) => (
              <Input
                id={id}
                type="email"
                value={draft.purchasing_email}
                maxLength={100}
                placeholder={t("newBranch.purchasingEmailExample")}
                onChange={(event) => set("purchasing_email", event.target.value)}
              />
            )}
          </FormField>
        </div>
      </section>
    </Dialog>
  );
}
